import { useState, useRef, useEffect } from 'react';
import { useSchemaDesignerStore, TableData, Column, ColumnType } from '@/stores/schema-designer-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  RiSparklingFill,
  RiSendPlaneLine,
  RiDeleteBinLine,
  RiLoaderLine,
  RiRobot2Line,
  RiUserLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tablesCreated?: number;
}

export const AiSchemaAssistant = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { addTable, nodes, edges } = useSchemaDesignerStore();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const convertAITableToTableData = (aiTable: any): TableData => {
    const columns: Column[] = aiTable.columns.map((col: any, index: number) => ({
      id: `column-${Date.now()}-${index}`,
      name: col.name,
      type: col.type as ColumnType,
      nullable: col.nullable ?? true,
      primaryKey: col.primaryKey ?? false,
      unique: col.unique ?? false,
      autoIncrement: col.autoIncrement ?? false,
      length: col.length,
      precision: col.precision,
      scale: col.scale,
      defaultValue: col.defaultValue,
      comment: col.comment,
    }));

    return {
      name: aiTable.name,
      columns,
      indexes: [],
      comment: aiTable.comment,
    };
  };

  const handleSubmit = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    try {
      // Call AI backend to generate schema design
      const currentSchema = {
        nodes: nodes.map(node => ({
          data: {
            name: node.data.name,
            columns: node.data.columns.map(col => ({
              name: col.name,
              type: col.type,
            })),
          },
        })),
        edges,
      };

      const result = await api.generateSchemaDesign(userMessage.content, currentSchema);

      if (!result.tables || result.tables.length === 0) {
        const assistantMessage: Message = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: result.explanation || `I understand you want to work on your schema, but I need more details. Here are some examples:

- "Create a users table with email, password, and created_at"
- "Add a products table with name, description, price, and stock_quantity"
- "I need an e-commerce system with users, products, orders, and order_items"
- "Build a blog schema with posts, comments, and tags"
- "Add a relationships between users and orders"

Try describing what you'd like to create!`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Apply the AI-generated tables to canvas
        let tablesAdded = 0;
        const results: string[] = [];

        for (let i = 0; i < result.tables.length; i++) {
          const aiTable = result.tables[i];

          // Check if table already exists
          const existingTable = nodes.find(
            node => node.data.name.toLowerCase() === aiTable.name.toLowerCase()
          );

          if (existingTable) {
            results.push(`⚠️ Table "${aiTable.name}" already exists, skipped.`);
            continue;
          }

          // Convert and add table with a small delay to ensure unique IDs
          const tableData = convertAITableToTableData(aiTable);

          // Add a tiny delay to ensure unique timestamps (only for subsequent tables)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 10));
          }

          addTable(tableData);
          tablesAdded++;
          results.push(`✓ Created "${aiTable.name}" with ${aiTable.columns.length} columns`);
        }

        const responseContent = [
          result.explanation,
          '',
          ...results,
        ].join('\n');

        const assistantMessage: Message = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: responseContent,
          timestamp: new Date(),
          tablesCreated: tablesAdded,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        if (tablesAdded > 0) {
          toast.success(`Created ${tablesAdded} ${tablesAdded === 1 ? 'table' : 'tables'}`);
        } else {
          toast.info('No new tables created');
        }
      }
    } catch (error) {
      console.error('AI processing error:', error);
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      toast.error('Failed to process request');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    toast.success('Conversation cleared');
  };

  const welcomeMessage = messages.length === 0;

  return (
    <Card className="h-full flex flex-col border-border/60">
      <CardHeader className="pb-3 border-b border-border/40">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <RiSparklingFill className="h-5 w-5 text-primary" />
            AI Schema Designer
          </CardTitle>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearConversation}
              className="h-7 px-2 text-xs gap-1"
            >
              <RiDeleteBinLine className="h-3 w-3" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {welcomeMessage ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 py-8">
              <div className="p-4 bg-primary/10 rounded-full">
                <RiRobot2Line className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2 max-w-md">
                <h3 className="font-semibold text-lg">AI-Powered Schema Design</h3>
                <p className="text-sm text-muted-foreground">
                  Describe your database schema in natural language. The AI understands your intent and generates complete schemas with proper relationships.
                </p>
              </div>
              <div className="space-y-2 text-xs text-left bg-muted/50 rounded-lg p-4 max-w-md w-full">
                <p className="font-medium text-foreground mb-2">Try these examples:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>- "Create a users table with email, password, and timestamps"</li>
                  <li>- "I need an e-commerce system with users, products, and orders"</li>
                  <li>- "Build a blog schema with posts, comments, and tags"</li>
                  <li>- "Add a social media feature with posts, likes, and followers"</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-3',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <RiRobot2Line className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'rounded-lg px-4 py-2.5 max-w-[85%]',
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    )}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                    <p className={cn(
                      "text-xs mt-1.5",
                      message.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <RiUserLine className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
              {isProcessing && (
                <div className="flex gap-3 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <RiLoaderLine className="h-4 w-4 text-primary animate-spin" />
                  </div>
                  <div className="rounded-lg px-4 py-2.5 bg-muted">
                    <p className="text-sm text-muted-foreground">Processing...</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="border-t border-border/40 p-4">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your schema in natural language... (e.g., 'I need an e-commerce system with users, products, and orders')"
              className="resize-none min-h-[60px] max-h-[120px]"
              disabled={isProcessing}
            />
            <Button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing}
              size="sm"
              className="px-3 self-end"
            >
              {isProcessing ? (
                <RiLoaderLine className="h-4 w-4 animate-spin" />
              ) : (
                <RiSendPlaneLine className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
